import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { APPLICATION_STATUS, FIRM_APPLICATION_TYPES, FIRM_SERVICES, FIRM_SERVICE_FOR, LEAD_BASED_PAINT } from '@shared/utils/app-static-data';
import { ConfirmationDialogueComponent } from 'app/components/common/confirmation-dialogue/confirmation-dialogue.component';
import { DocumentUploadDialogComponent, IDocumentUploadDialogResult } from 'app/components/common/document-upload-dialog/document-upload-dialog.component';
import { FeatureFlagsService } from 'app/services/core/feature-flags.service';
import { IFirmApplication } from 'app/models/firms/firm-application';
import { IDocument } from 'app/models/document/document';
import { DOCUMENT_CATEGORIES } from 'app/models/document/document-categories';
import { DocumentService } from 'app/services/document/document.service';
import { IFirmApplicationPerson } from 'app/models/firms/firm-application-person';
import { IFirmCertificate } from 'app/models/firms/firm-certificate';
import { IFirm } from 'app/models/firms/firm';
import { CommonService } from 'app/services/common/common.service';
import { SharedService } from 'app/services/core/shared.service';
import { FirmService } from 'app/services/firms/firm.service';
import { PersonService } from 'app/services/person/person.service';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { FirmApplicationPersonDialogComponent } from '../firm-application-person-dialog/firm-application-person-dialog.component';

interface IFirmApplicationDialogData {
  applicationId?: number | null;
  firmId?: number | null;
  workflow?: 'new' | 'link' | 'renewal';
  previousApplication?: string | null;
}

interface IFirmLinkSummary {
  firm: IFirm;
  addresses: any[];
  primaryContacts: any[];
  firmContacts: any[];
}

interface IRenewalOption {
  label: string;
  certificate: IFirmCertificate;
  applicationType: string;
}

type IFirmApplicationWorkflow = 'chooser' | 'new' | 'link' | 'renewal';

interface IWizardValidationError {
  field: string;
  label: string;
  step: string;
}

interface IValidationFeedbackGroup {
  step: string;
  errors: IWizardValidationError[];
}

@Component({
  selector: 'app-firm-application',
  templateUrl: './firm-application.component.html',
  styleUrls: ['./firm-application.component.scss']
})
export class FirmApplicationComponent implements OnInit {
  public readonly FIRM_APPLICATION_TYPES = FIRM_APPLICATION_TYPES;
  public readonly reviewPlaceholder = 'Not Provided';
  public loading = true;
  public saving = false;
  public didPersistChange = false;
  public currentStep = 0;
  public selectedWorkflow: IFirmApplicationWorkflow = 'new';
  public uiData = LEAD_BASED_PAINT;
  public application!: IFirmApplication;
  public applicationForm: FormGroup;
  public attestationForm: FormGroup;
  public people: IFirmApplicationPerson[] = [];
  public documents: IDocument[] = [];
  public peopleFilterString = '';
  public filteredPeople: IFirmApplicationPerson[] = [];
  public documentsFilterString = '';
  public filteredDocuments: IDocument[] = [];
  public linkSearchResults: Array<{ name: string; isPrivate: boolean }> = [];
  public linkSummary: IFirmLinkSummary | null = null;
  public linkLookupLoading = false;
  public linkLookupFailed = false;
  public linkLookupMessage = '';
  public renewalOptions: IRenewalOption[] = [];
  public validationSummaryRequested = false;
  public isFeedbackCollapsed = false;
  public readonly serviceForTypes = Object.values(FIRM_SERVICE_FOR);
  public readonly applicationTypes = [FIRM_APPLICATION_TYPES.lbpa, FIRM_APPLICATION_TYPES.rrp];

  @ViewChild('validationSummaryCard') validationSummaryCard?: ElementRef<HTMLElement>;

  private readonly standardStepLabels = ['Application Information', 'Firm Information', 'Certified Individuals', 'Documents', 'Review & Submit'];
  private readonly linkStepLabels = ['Firm Information', 'Signature and Acknowledgments'];
  private readonly submittedLinkStepLabels = ['Firm Information', 'Signature and Acknowledgments', 'Confirmation'];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: IFirmApplicationDialogData,
    private dialogRef: MatDialogRef<FirmApplicationComponent>,
    private dialog: MatDialog,
    private featureFlags: FeatureFlagsService,
    public sharedService: SharedService,
    private commonService: CommonService,
    private readonly documentService: DocumentService,
    private firmService: FirmService,
    private personService: PersonService,
    private toastr: ToastrService,
  ) {
    this.applicationForm = this.createApplicationForm();
    this.attestationForm = new FormGroup({
      attestation: new FormControl(false, Validators.requiredTrue),
      esignature: new FormControl('', Validators.required),
    });
  }

  ngOnInit(): void {
    if (!this.featureFlags.enableFirms) {
      this.dialogRef.close(false);
      return;
    }

    this.selectedWorkflow = this.resolveInitialWorkflow();

    if (this.data.applicationId) {
      this.loadExistingApplication(this.data.applicationId);
      return;
    }

    this.initializeNewApplication();
  }

  get dialogTitle(): string {
    const baseTitle = this.selectedWorkflow === 'renewal'
      ? 'Firm Renewal Application'
      : this.isLinkWorkflow
        ? 'Firm Link Request'
        : 'Firm Application';
    const applicationStatus = this.displayApplicationStatus;

    if (this.showWorkflowChooser || !applicationStatus) {
      return baseTitle;
    }

    return baseTitle + ' - ' + applicationStatus;
  }

  get displayApplicationStatus(): string {
    if (!this.application?.applicationStatus) {
      return '';
    }

    return this.isApplicationExpired(this.application) ? 'Expired' : this.application.applicationStatus;
  }

  get showApplicationStatusSummary(): boolean {
    return !!this.application?.id && !!this.displayApplicationStatus;
  }

  get applicationStatusDateLabel(): string {
    const status = this.normalizeStatus(this.application?.applicationStatus);

    if (status === this.normalizeStatus(APPLICATION_STATUS.approved)) {
      return 'Approved On';
    }

    if (status === this.normalizeStatus(APPLICATION_STATUS.cancelled)) {
      return 'Cancelled On';
    }

    return '';
  }

  get applicationStatusDate(): string | null {
    const status = this.normalizeStatus(this.application?.applicationStatus);

    if (status === this.normalizeStatus(APPLICATION_STATUS.approved)) {
      return this.application?.approvalDate || null;
    }

    if (status === this.normalizeStatus(APPLICATION_STATUS.cancelled)) {
      return this.application?.lastUpdated || null;
    }

    return null;
  }

  get isLinkWorkflow(): boolean {
    return this.selectedWorkflow === 'link';
  }

  get showWorkflowChooser(): boolean {
    return this.selectedWorkflow === 'chooser';
  }

  get canReturnToChooser(): boolean {
    return !this.showWorkflowChooser
      && !this.data.applicationId
      && !this.data.firmId
      && this.selectedWorkflow !== 'renewal'
      && (this.application?.id || 0) === 0;
  }

  get linkPrimaryContact(): any | null {
    return this.linkSummary?.primaryContacts?.[0] ?? null;
  }

  get linkApprovalContactEmails(): string[] {
    const readEmail = (contact: any): string => {
      return (
        contact?.contactInformation?.email
        || contact?.contactInformation?.Email
        || contact?.email
        || contact?.Email
        || ''
      ).trim();
    };

    const personPocEmails = (this.linkSummary?.primaryContacts || [])
      .map((contact) => readEmail(contact));

    const firmContactEmails = (this.linkSummary?.firmContacts || [])
      .map((contact) => readEmail(contact));

    return personPocEmails
      .concat(firmContactEmails)
      .filter((email, index, list) => email.length > 0
        && list.findIndex((candidate) => candidate.toLowerCase() === email.toLowerCase()) === index);
  }

  get hasLinkApprovalContactEmails(): boolean {
    return this.linkApprovalContactEmails.length > 0;
  }

  get hasMultipleLinkApprovalContactEmails(): boolean {
    return this.linkApprovalContactEmails.length > 1;
  }

  get canEdit(): boolean {
    if (!this.application) {
      return false;
    }

    return this.isDraftStatus(this.application.applicationStatus) && !this.isApplicationExpired(this.application);
  }

  get isRenewalApplication(): boolean {
    return this.selectedWorkflow === 'renewal'
      || this.application?.renewal === true
      || this.applicationForm.get('renewal')?.value === true;
  }

  get showLbpaFields(): boolean {
    return this.applicationForm.get('applicationType')?.value === FIRM_APPLICATION_TYPES.lbpa;
  }

  get showPhysicalCounty(): boolean {
    return this.applicationForm.get('firmPhysicalState')?.value === 'OR';
  }

  get showMailingCounty(): boolean {
    return this.showMailingAddress && this.applicationForm.get('firmMailingState')?.value === 'OR';
  }

  get certifiedIndividualTableColumns(): string[] {
    const columns = ['name'];
    return this.canEdit ? columns.concat('actions') : columns;
  }

  get documentTableColumns(): string[] {
    return ['fileName', 'description', 'association', 'actions'];
  }

  get reviewCertifiedIndividualTableColumns(): string[] {
    return ['name', 'primary', 'secondary'];
  }

  get reviewDocumentTableColumns(): string[] {
    return ['type', 'fileName', 'description', 'association', 'status'];
  }

  get showMailingAddress(): boolean {
    return this.applicationForm.get('mailingIsPhysical')?.value !== true;
  }

  get isMailingSameAsPhysical(): boolean {
    return this.applicationForm.get('mailingIsPhysical')?.value === true;
  }

  get isSubmitted(): boolean {
    return this.application?.applicationStatus === APPLICATION_STATUS.submitted;
  }

  get applicationTypeLabel(): string {
    if (this.isLinkWorkflow) {
      return 'Link Existing Firm';
    }

    return this.applicationForm.get('applicationType')?.value === FIRM_APPLICATION_TYPES.rrp
      ? 'Renovation, Repair and Painting'
      : 'Lead-Based Paint Activities';
  }

  get firmIdentifierLabel(): string {
    return this.applicationForm.get('applicationType')?.value === FIRM_APPLICATION_TYPES.rrp
      ? 'Property Management License #'
      : 'CCB License #';
  }

  get certifiedIndividualPrimaryLabel(): string {
    return this.showLbpaFields ? 'Discipline' : 'Training Provider';
  }

  get certifiedIndividualSecondaryLabel(): string {
    return this.showLbpaFields ? 'Fee' : 'Training Date';
  }

  get selectedFirmServiceLabels(): string[] {
    const rawValue = this.applicationForm.getRawValue();
    const services: string[] = [];

    if (rawValue.inspections) {
      services.push(FIRM_SERVICES.inspections);
    }

    if (rawValue.clearanceTesting) {
      services.push(FIRM_SERVICES.clearanceTesting);
    }

    if (rawValue.leadHazardScreening) {
      services.push(FIRM_SERVICES.leadHazardScreening);
    }

    if (rawValue.riskAssessments) {
      services.push(FIRM_SERVICES.riskAssessments);
    }

    if (rawValue.abatement) {
      services.push(FIRM_SERVICES.abatement);
    }

    return services;
  }

  get hasLinkedFirmReviewData(): boolean {
    return !!this.linkSummary?.firm
      || !!this.application?.firmId
      || this.hasText(this.applicationForm.get('ccb')?.value)
      || this.hasText(this.applicationForm.get('firmServices')?.value)
      || this.hasText(this.applicationForm.get('coverage')?.value);
  }

  get linkFirmIdentifierValue(): string {
    return this.displayReviewValue(
      this.linkSummary?.firm?.propertyManagementLicense
      || this.linkSummary?.firm?.ccb
      || this.applicationForm.get('ccb')?.value
    );
  }

  get linkFirmServicesValue(): string {
    return this.displayReviewValue(this.linkSummary?.firm?.servicesAvailable || this.applicationForm.get('firmServices')?.value);
  }

  get linkFirmCoverageValue(): string {
    return this.displayReviewValue(this.linkSummary?.firm?.coverage || this.applicationForm.get('coverage')?.value);
  }

  get linkFirmPrivateValue(): string {
    if (!this.hasLinkedFirmReviewData) {
      return this.reviewPlaceholder;
    }

    const isPrivate = this.linkSummary?.firm
      ? this.linkSummary.firm.isPrivate === true
      : this.applicationForm.get('isPublic')?.value === true;

    return this.displayYesNo(isPrivate);
  }

  statusColor(status: string): string {
    return this.sharedService.getStatusColor(status);
  }

  displayReviewValue(value: unknown, emptyValue = this.reviewPlaceholder): string {
    if (value === null || value === undefined) {
      return emptyValue;
    }

    if (typeof value === 'number') {
      return value.toString();
    }

    const normalized = value.toString().trim();
    return normalized.length > 0 ? normalized : emptyValue;
  }

  displayYesNo(value: boolean | null | undefined, trueLabel = 'Yes', falseLabel = 'No'): string {
    return value === true ? trueLabel : falseLabel;
  }

  getFullName(firstName: string | null | undefined, middleName: string | null | undefined, lastName: string | null | undefined): string {
    const parts = [firstName, middleName, lastName]
      .map((value) => (value || '').trim())
      .filter((value) => value.length > 0);

    return parts.length > 0 ? parts.join(' ') : this.reviewPlaceholder;
  }

  getDocumentTypeLabel(document: IDocument): string {
    const fileName = (document.fileName || '').trim();
    const extensionMatch = fileName.match(/\.([^.]+)$/);
    if (extensionMatch?.[1]) {
      return extensionMatch[1].toUpperCase();
    }

    if (this.hasText(document.fileType)) {
      const fileType = document.fileType.trim();
      const normalized = (fileType.includes('/') ? fileType.split('/').pop() || fileType : fileType)
        .replace(/[._-]+/g, ' ')
        .trim();

      return normalized.length > 0 ? normalized.toUpperCase() : this.reviewPlaceholder;
    }

    return this.reviewPlaceholder;
  }

  getDocumentStatusLabel(document: IDocument): string {
    return document.id > 0 || this.hasText(document.fileName)
      ? 'Uploaded'
      : this.reviewPlaceholder;
  }

  close(): void {
    if (!this.canEdit) {
      this.dialogRef.close(this.didPersistChange);
      return;
    }

    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '420px',
      data: {
        title: 'Exit without saving?',
        message: 'Any unsaved data will be lost.\n\nAre you sure you want to exit?',
      }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.dialogRef.close(this.didPersistChange);
      }
    });
  }

  searchFirmNames(value: string): void {
    if (!this.isLinkWorkflow) {
      return;
    }

    const searchTerm = (value || '').trim();
    if (!this.selectedLinkFirmNameMatches(searchTerm)) {
      this.resetLinkSelectionState();
    }

    if (searchTerm.length < 4) {
      this.linkSearchResults = [];
      return;
    }

    this.firmService.searchFirmNames(searchTerm).subscribe({
      next: (results) => {
        this.linkSearchResults = results || [];
      },
      error: () => {
        this.linkSearchResults = [];
      }
    });
  }

  onStepSelectionChange(event: StepperSelectionEvent): void {
    if (this.loading || this.saving) {
      return;
    }

    this.navigateToStep(event.selectedIndex);
  }

  loadLinkFirmDetails(showValidationFeedback = false, continuation?: () => void): void {
    if (!this.isLinkWorkflow || this.linkLookupLoading) {
      return;
    }

    const firmNameControl = this.applicationForm.get('firmName');
    const firmName = (firmNameControl?.value || '').trim();
    this.clearLinkLookupError();

    if (!firmName) {
      this.resetLinkSelectionState();

      if (showValidationFeedback) {
        this.validationSummaryRequested = true;
        firmNameControl?.markAsTouched();
        this.handleValidationFailure(this.getStepValidationErrors(0), 'Enter a Firm name before submitting the link request.');
      }

      return;
    }

    if (this.linkSummary && this.selectedLinkFirmNameMatches(firmName)) {
      continuation?.();
      return;
    }

    this.linkLookupLoading = true;
    this.firmService.getFirmByName(firmName).subscribe({
      next: (firm) => {
        if (!firm) {
          this.linkLookupLoading = false;
          this.resetLinkSelectionState();
          this.setLinkLookupError('No Firm matched the name provided.');

          if (showValidationFeedback) {
            this.validationSummaryRequested = true;
            this.handleValidationFailure(this.getStepValidationErrors(0), 'Select a matching Firm before submitting the link request.');
          }

          return;
        }

        forkJoin({
          addresses: this.firmService.getFirmAddresses(firm.id).pipe(catchError(() => of([]))),
          primaryContacts: this.personService.GetAllFirmPOCPeopleContactInformation(firm.id).pipe(catchError(() => of([]))),
          firmContacts: this.firmService.getFirmContactInformation(firm.id).pipe(catchError(() => of([]))),
        }).subscribe({
          next: (details) => {
            this.linkSummary = {
              firm,
              addresses: details.addresses,
              primaryContacts: details.primaryContacts,
              firmContacts: details.firmContacts,
            };
            this.applyLinkedFirmToApplication();
            this.linkLookupFailed = false;
            this.linkLookupMessage = '';
            this.linkLookupLoading = false;
            continuation?.();
          },
          error: () => {
            this.linkLookupLoading = false;
            this.resetLinkSelectionState();
            this.setLinkLookupError('Unable to load Firm details for linking.');
            this.toastr.error('Unable to load Firm details for linking.');

            if (showValidationFeedback) {
              this.validationSummaryRequested = true;
              this.handleValidationFailure(this.getStepValidationErrors(0), 'Unable to load the selected Firm before submitting the link request.');
            }
          }
        });
      },
      error: () => {
        this.linkLookupLoading = false;
        this.resetLinkSelectionState();
        this.setLinkLookupError('Unable to find the Firm to link.');
        this.toastr.error('Unable to find the Firm to link.');

        if (showValidationFeedback) {
          this.validationSummaryRequested = true;
          this.handleValidationFailure(this.getStepValidationErrors(0), 'Unable to find the selected Firm before submitting the link request.');
        }
      }
    });
  }

  selectNewApplication(): void {
    this.beginWorkflow('new');
  }

  selectLinkApplication(): void {
    this.beginWorkflow('link');
  }

  showPreviousButton(): boolean {
    if (this.showWorkflowChooser || this.isSubmittedLinkConfirmationStep()) {
      return false;
    }

    return this.currentStep > 0 || this.canReturnToChooser;
  }

  showCancelButton(): boolean {
    if (this.showWorkflowChooser) {
      return false;
    }

    if (this.isLinkWorkflow) {
      return true;
    }

    return !!this.application?.id && this.canEdit;
  }

  isCancelButtonDisabled(): boolean {
    if (!this.application?.id) {
      return true;
    }

    const status = this.application.applicationStatus;
    return this.saving
      || status === APPLICATION_STATUS.cancelled
      || status === APPLICATION_STATUS.approved
      || this.isApplicationExpired(this.application);
  }

  getCancelButtonText(): string {
    if (this.isLinkWorkflow) {
      return 'Cancel Link Request';
    }

    return this.isSubmitted ? 'Withdraw' : 'Cancel';
  }

  showNextButton(): boolean {
    return !this.showWorkflowChooser && this.currentStep < this.getLastStepIndex();
  }

  showSubmitButton(): boolean {
    if (this.showWorkflowChooser || this.currentStep !== this.getLastStepIndex() || !this.canEdit) {
      return false;
    }

    if (!this.isLinkWorkflow) {
      return true;
    }

    return !this.linkLookupLoading && !this.linkLookupFailed && !!this.linkSummary;
  }

  isSubmitButtonDisabled(): boolean {
    if (!this.canEdit || this.saving) {
      return true;
    }

    if (!this.isLinkWorkflow) {
      return false;
    }

    return this.linkLookupLoading || this.linkLookupFailed || !this.linkSummary;
  }

  goToNextStep(): void {
    if (this.saving || this.loading) {
      return;
    }

    this.navigateToStep(this.currentStep + 1);
  }

  goToPreviousStep(): void {
    if (this.currentStep === 0 && this.canReturnToChooser) {
      this.selectedWorkflow = 'chooser';
      this.resetWorkflowState();
      return;
    }

    if (this.currentStep > 0) {
      this.navigateToStep(this.currentStep - 1);
    }
  }

  hasValidationErrors(): boolean {
    return this.validationSummaryRequested && this.getValidationErrors().length > 0;
  }

  hasFeedbackContent(): boolean {
    return this.firmService.shouldDisplayApplicationDeficiencies(this.application) || this.hasValidationErrors();
  }

  toggleFeedbackPanel(): void {
    this.isFeedbackCollapsed = !this.isFeedbackCollapsed;
  }

  stepHasValidationErrors(stepIndex: number): boolean {
    return this.validationSummaryRequested && this.getStepValidationErrors(stepIndex).length > 0;
  }

  getValidationFeedbackGroups(): IValidationFeedbackGroup[] {
    if (!this.validationSummaryRequested) {
      return [];
    }

    const validationErrors = this.getValidationErrors();
    if (validationErrors.length === 0) {
      return [];
    }

    const groupedErrors = new Map<string, IWizardValidationError[]>();
    validationErrors.forEach((error) => {
      const existing = groupedErrors.get(error.step) || [];
      if (!existing.some((candidate) => candidate.label === error.label)) {
        existing.push(error);
      }
      groupedErrors.set(error.step, existing);
    });

    const orderedGroups: IValidationFeedbackGroup[] = this.getStepLabels()
      .filter((stepLabel) => groupedErrors.has(stepLabel))
      .map((stepLabel) => ({ step: stepLabel, errors: groupedErrors.get(stepLabel) || [] }));

    groupedErrors.forEach((errors, step) => {
      if (!orderedGroups.some((group) => group.step === step)) {
        orderedGroups.push({ step, errors });
      }
    });

    return orderedGroups.filter((group) => group.errors.length > 0);
  }

  focusValidationFeedbackError(error: IWizardValidationError): void {
    const stepIndex = this.getStepLabels().findIndex((stepLabel) => stepLabel === error.step);
    if (stepIndex >= 0) {
      this.navigateToStep(stepIndex);
    }

    const selectors = this.resolveValidationFieldSelectors(error);
    setTimeout(() => {
      if (this.focusFirstVisibleBySelector(selectors)) {
        return;
      }

      if (this.focusFirstVisibleBySelector(this.getDefaultInvalidFieldSelectors())) {
        return;
      }

      this.getVisibleScrollContainer()?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 200);
  }

  getCombinedFeedback(): string {
    const sections: string[] = [];

    const deficiencyDescriptions = this.getPersistedDeficiencyDescriptions();
    if (deficiencyDescriptions.length > 0) {
      sections.push(deficiencyDescriptions.join('\n\n'));
    }

    const validationFeedback = this.getValidationFeedbackText();
    if (validationFeedback) {
      sections.push(validationFeedback);
    }

    return sections.join('\n\n').trim();
  }

  private getValidationFeedbackText(): string {
    const validationErrors = this.getValidationErrors();
    if (!this.validationSummaryRequested || validationErrors.length === 0) {
      return '';
    }

    const groupedErrors = new Map<string, string[]>();
    validationErrors.forEach((error) => {
      const existing = groupedErrors.get(error.step) || [];
      if (!existing.includes(error.label)) {
        existing.push(error.label);
      }
      groupedErrors.set(error.step, existing);
    });

    const lines = ['--- Missing Required Fields ---'];
    this.getStepLabels().forEach((stepLabel) => {
      const stepErrors = groupedErrors.get(stepLabel);
      if (!stepErrors || stepErrors.length === 0) {
        return;
      }

      lines.push(stepLabel + ':');
      stepErrors.forEach((label) => lines.push('- ' + label));
      lines.push('');
    });

    return lines.join('\n').trim();
  }

  getPersistedDeficiencyDescriptions(): string[] {
    return this.firmService.getUnresolvedApplicationDeficiencyDescriptions(this.application);
  }

  showApplicationControlError(controlName: string, errorName?: string): boolean {
    const control = this.applicationForm.get(controlName);
    if (!control) {
      return false;
    }

    if (errorName) {
      return control.hasError(errorName) && this.hasValidationInteraction(control);
    }

    return control.invalid && this.hasValidationInteraction(control);
  }

  showAttestationError(controlName: string, errorName?: string): boolean {
    const control = this.attestationForm.get(controlName);
    if (!control) {
      return false;
    }

    if (errorName) {
      if (errorName === 'requiredTrue') {
        return (control.hasError('requiredTrue') || control.hasError('required')) && this.hasValidationInteraction(control);
      }

      return control.hasError(errorName) && this.hasValidationInteraction(control);
    }

    return control.invalid && this.hasValidationInteraction(control);
  }

  showMailingRequiredError(controlName: string): boolean {
    const control = this.applicationForm.get(controlName);
    return this.showMailingAddress && !this.hasText(control?.value) && this.hasValidationInteraction(control);
  }

  showRenewalSelectionError(): boolean {
    const control = this.applicationForm.get('previousApplication');
    return this.isRenewalApplication && !this.hasText(control?.value) && this.hasValidationInteraction(control);
  }

  showCountyRequiredError(controlName: 'firmPhysicalCounty' | 'firmMailingCounty'): boolean {
    const stateControlName = controlName === 'firmPhysicalCounty' ? 'firmPhysicalState' : 'firmMailingState';
    const control = this.applicationForm.get(controlName);
    return this.applicationForm.get(stateControlName)?.value === 'OR'
      && !this.hasText(control?.value)
      && this.hasValidationInteraction(control);
  }

  saveDraft(showToast = true, continuation?: () => void): void {
    if (!this.canEdit || this.isLinkWorkflow) {
      return;
    }

    this.syncApplicationFromForms();
    this.persistApplication(this.application.applicationStatus || APPLICATION_STATUS.inProgress, showToast ? 'Draft saved.' : null, continuation);
  }

  submitApplication(): void {
    if (this.isLinkWorkflow) {
      this.submitLinkApplication();
      return;
    }

    this.validationSummaryRequested = true;
    this.applicationForm.markAllAsTouched();
    this.applicationForm.updateValueAndValidity({ emitEvent: false });
    this.attestationForm.markAllAsTouched();

    const validationErrors = this.getValidationErrors();
    if (validationErrors.length > 0) {
      this.handleValidationFailure(validationErrors, 'Complete the required fields before submitting.');
      return;
    }

    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '420px',
      data: {
        title: 'Firm Application Submission Confirmation',
        message: '<strong>You are about to submit your Firm Application.</strong>\n\nAre you sure you want to submit this application?',
      }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }

      this.syncApplicationFromForms();
      this.persistApplication(APPLICATION_STATUS.submitted, 'Firm application submitted.', () => this.dialogRef.close(true));
    });
  }

  submitLinkApplication(): void {
    this.validationSummaryRequested = true;
    this.applicationForm.get('firmName')?.markAsTouched();
    this.attestationForm.markAllAsTouched();

    if (!this.linkSummary && this.hasText(this.applicationForm.get('firmName')?.value)) {
      this.loadLinkFirmDetails(true, () => this.submitLinkApplication());
      return;
    }

    const validationErrors = this.getValidationErrors();
    if (validationErrors.length > 0) {
      this.handleValidationFailure(validationErrors, 'Complete the required fields before submitting the link request.');
      return;
    }

    const openConfirmation = () => {
      const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
        width: '420px',
        data: {
          title: 'Firm Link Request Confirmation',
          message: `<strong>You are about to submit a Firm account-link request.</strong>\n\n${this.getLinkApprovalRecipientSummary()}\n\nThis request will be reviewed and processed before access can be granted, and you will be notified by email once your request has been reviewed.\n\nAre you sure you want to submit this request?`,
        }
      });

      dialogRef.afterClosed().subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }

        this.syncApplicationFromForms();
        this.persistApplication(APPLICATION_STATUS.submitted, 'Firm link request submitted. You will be notified after review.', () => {
          this.applicationForm.disable({ emitEvent: false });
          this.attestationForm.disable({ emitEvent: false });
          this.currentStep = this.getLastStepIndex();
        });
      });
    };

    const linkedFirmId = this.linkSummary?.firm?.id || 0;
    if (linkedFirmId > 0) {
      this.guardDuplicateFirmLinkSubmission(linkedFirmId, openConfirmation);
      return;
    }

    openConfirmation();
  }

  cancelApplication(): void {
    if (!this.application) {
      return;
    }

    const isLinkRequest = this.isLinkWorkflow;
    const message = isLinkRequest
      ? 'Are you sure you want to cancel this link request? This action will mark the link request as cancelled.'
      : this.isSubmitted
        ? '<strong>You are about to withdraw your Firm Application.</strong>\nYour application will return to <strong>In Progress</strong> so you can edit it again.\n\nAre you sure?'
        : 'You are about to cancel this Firm Application.\n\nAre you sure you want to continue?';
    const title = isLinkRequest
      ? 'Cancel Link Request?'
      : this.isSubmitted
        ? 'Withdraw Firm Application Confirmation'
        : 'Cancel Firm Application Confirmation';

    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '420px',
      data: { title, message }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }

      this.syncApplicationFromForms();
      const nextStatus = isLinkRequest
        ? APPLICATION_STATUS.cancelled
        : this.isSubmitted
          ? APPLICATION_STATUS.inProgress
          : APPLICATION_STATUS.cancelled;
      const successMessage = isLinkRequest
        ? 'Link request cancelled.'
        : this.isSubmitted
          ? 'Application withdrawn.'
          : 'Application cancelled.';

      this.persistApplication(nextStatus, successMessage, () => {
        if (isLinkRequest) {
          this.dialogRef.close(true);
          return;
        }

        this.dialogRef.close(true);
      });
    });
  }

  addPerson(): void {
    this.openPersonDialog(this.createEmptyPerson());
  }

  editPerson(person: IFirmApplicationPerson): void {
    this.openPersonDialog(person);
  }

  deletePerson(person: IFirmApplicationPerson): void {
    if (!this.canEdit || !person.id) {
      return;
    }

    if (this.documents.some((document) => document.firmApplicationPersonId === person.id)) {
      this.toastr.error('Remove or reassign documents associated with this certified individual before deleting them.');
      return;
    }

    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '420px',
      data: {
        title: 'Delete Certified Individual',
        message: 'Are you sure you want to remove <strong>' + person.firstName + ' ' + person.lastName + '</strong> from this application?',
      }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }

      this.firmService.deleteFirmApplicationPerson(person.id).subscribe({
        next: () => {
          this.setPeople(this.people.filter((currentPerson) => currentPerson.id !== person.id));
          this.didPersistChange = true;
        },
        error: () => this.toastr.error('Unable to remove the certified individual.'),
      });
    });
  }

  openDocumentUploadDialog(): void {
    if (!this.canEdit) {
      return;
    }

    const dialogRef = this.dialog.open(DocumentUploadDialogComponent, {
      width: this.sharedService.isMobile ? '95%' : '560px',
      autoFocus: false,
      data: {
        title: 'Upload Firm Application Document',
        allowAssociation: true,
        people: this.people
          .filter((person) => person.id > 0)
          .map((person) => ({
            id: person.id,
            firstName: person.firstName,
            lastName: person.lastName,
          })),
      },
    });

    dialogRef.afterClosed().subscribe((result: IDocumentUploadDialogResult | undefined) => {
      if (!result?.file) {
        return;
      }

      this.ensureApplicationSaved(() => {
        this.saveDocumentFile(result.file, result.description, result.applicationPersonId);
      });
    });
  }

  deleteDocument(document: IDocument): void {
    if (!this.canEdit || !document.id) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '420px',
      data: {
        title: 'Delete Application Document',
        message: 'Are you sure you want to remove <strong>' + document.fileName + '</strong> from this application?',
      }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }

      this.documentService.deleteDocument(document.id).subscribe({
        next: () => {
          this.setDocuments(this.documents.filter((currentDocument) => currentDocument.id !== document.id));
          this.didPersistChange = true;
        },
        error: () => this.toastr.error('Unable to delete the document.'),
      });
    });
  }

  onStateSelectionChange(controlName: 'firmPhysicalState' | 'firmMailingState'): void {
    const countyControlName = controlName === 'firmPhysicalState' ? 'firmPhysicalCounty' : 'firmMailingCounty';

    if (this.applicationForm.get(controlName)?.value !== 'OR') {
      this.applicationForm.get(countyControlName)?.setValue('');
    }

    this.applicationForm.get(countyControlName)?.updateValueAndValidity({ emitEvent: false });
  }

  filterPeopleTable(): void {
    const filterText = this.peopleFilterString.trim().toLowerCase();
    this.filteredPeople = this.people.filter((person) => {
      if (!filterText) {
        return true;
      }

      return [
        person.person?.id?.toString() || '',
        person.firstName || '',
        person.lastName || '',
        person.discipline || '',
        person.atp || '',
        person.certificateId || '',
      ].join(' ').toLowerCase().includes(filterText);
    });
  }

  filterDocumentTable(): void {
    const filterText = this.documentsFilterString.trim().toLowerCase();
    this.filteredDocuments = this.documents.filter((document) => {
      if (!filterText) {
        return true;
      }

      return [
        document.fileName || '',
        document.fileDescription || '',
        this.getPersonLabel(document.firmApplicationPersonId),
      ].join(' ').toLowerCase().includes(filterText);
    });
  }

  downloadDocument(document: IDocument): void {
    if (!document?.id) {
      return;
    }

    this.documentService.downloadDocument(document).subscribe({
      error: (error) => this.toastr.error(error?.error ?? 'Unable to download the document.'),
    });
  }

  getPersonLabel(personId: number | null): string {
    if (!personId) {
      return 'Application document';
    }

    const person = this.people.find((currentPerson) => currentPerson.id === personId);
    return person ? person.firstName + ' ' + person.lastName : 'Associated individual';
  }

  applyRenewalSelection(controlNumber: string): void {
    const option = this.renewalOptions.find((renewalOption) => renewalOption.certificate.controlNumber === controlNumber);
    if (!option) {
      return;
    }

    this.applicationForm.patchValue({
      applicationType: option.applicationType,
      previousApplication: option.certificate.controlNumber,
      renewal: true,
    });
  }

  private initializeNewApplication(): void {
    this.resetWorkflowState();

    if (this.showWorkflowChooser) {
      this.loading = false;
      return;
    }

    if (this.selectedWorkflow === 'renewal' && this.data.firmId) {
      this.loadRenewalContext(this.data.firmId);
      return;
    }

    this.loading = false;
  }

  private loadExistingApplication(applicationId: number): void {
    forkJoin({
      application: this.firmService.getFirmApplicationById(applicationId),
      people: this.firmService.getFirmApplicationPeople(applicationId).pipe(catchError(() => of([]))),
      documents: this.documentService.getDocuments({ ownerType: 'FirmApplication', ownerId: applicationId }).pipe(catchError(() => of([]))),
    }).subscribe({
      next: (result) => {
        this.application = result.application;
        this.selectedWorkflow = result.application.applicationType === FIRM_APPLICATION_TYPES.link ? 'link' : 'new';
        this.peopleFilterString = '';
        this.documentsFilterString = '';
        this.setPeople(result.people);
        this.setDocuments(result.documents);
        this.patchFormsFromApplication();

        if (this.isLinkWorkflow && this.application.firmId) {
          this.loadSavedLinkSummary(this.application.firmId);
        }

        if (!this.canEdit) {
          this.applicationForm.disable();
          this.attestationForm.disable();
        }

          this.currentStep = this.getInitialStepIndex();
          this.linkLookupLoading = false;
        this.validationSummaryRequested = false;

        this.loading = false;
      },
      error: () => {
        this.toastr.error('Unable to load the Firm application.');
        this.dialogRef.close(false);
      }
    });
  }

  private loadRenewalContext(firmId: number): void {
    this.firmService.getFirmWorkspace(firmId).subscribe({
      next: (workspace) => {
        this.application.firmId = workspace.firm.id;
        this.application.firm = workspace.firm;
        this.application.firmName = workspace.firm.name;
        this.application.ccb = workspace.firm.ccb;
        this.application.isPublic = workspace.firm.isPrivate === true;
        this.application.firmServices = workspace.firm.servicesAvailable ?? null;
        this.application.coverage = workspace.firm.coverage ?? null;
        this.application.renewal = true;
        this.renewalOptions = this.buildRenewalOptions(workspace.certificates || []);

        const physicalAddress = workspace.addresses.find((address) => address.isCurrentPhysical)?.address;
        const mailingAddress = workspace.addresses.find((address) => address.isCurrentMailing)?.address;
        const firmContact = workspace.contacts[0]?.contactInformation;
        const poc = workspace.people.find((person) => person.isPoc);

        this.application.contactTitle = poc?.person?.title ?? null;
        this.application.contactFirstName = poc?.person?.firstName ?? null;
        this.application.contactMiddleName = poc?.person?.middleName ?? null;
        this.application.contactLastName = poc?.person?.lastName ?? null;
        this.application.contactPhone = poc?.contactInformation?.phone ?? null;
        this.application.contactAlternatePhone = poc?.contactInformation?.cell ?? null;
        this.application.contactFaxPhone = poc?.contactInformation?.fax ?? null;
        this.application.contactEmail = poc?.contactInformation?.email ?? null;
        this.application.contactConfirmEmail = poc?.contactInformation?.email ?? null;
        this.application.firmPhysicalStreet = physicalAddress?.street ?? null;
        this.application.firmPhysicalState = physicalAddress?.state ?? null;
        this.application.firmPhysicalCity = physicalAddress?.city ?? null;
        this.application.firmPhysicalZip = physicalAddress?.zip ?? null;
        this.application.firmPhysicalCounty = physicalAddress?.county ?? null;
        this.application.firmMailingStreet = mailingAddress?.street ?? null;
        this.application.firmMailingState = mailingAddress?.state ?? null;
        this.application.firmMailingCity = mailingAddress?.city ?? null;
        this.application.firmMailingZip = mailingAddress?.zip ?? null;
        this.application.firmMailingCounty = mailingAddress?.county ?? null;
        this.application.mailingIsPhysical = !!physicalAddress && !!mailingAddress
          && physicalAddress.street === mailingAddress.street
          && physicalAddress.city === mailingAddress.city
          && physicalAddress.state === mailingAddress.state
          && physicalAddress.zip === mailingAddress.zip;
        this.application.firmPhone = firmContact?.phone ?? null;
        this.application.firmAlternatePhone = firmContact?.cell ?? null;
        this.application.firmFaxPhone = firmContact?.fax ?? null;
        this.application.firmEmail = firmContact?.email ?? null;
        this.application.firmConfirmEmail = firmContact?.email ?? null;
        this.application.firmWebsite = firmContact?.website ?? null;
        this.application.inspections = workspace.credentials.some((credential) => credential.firmLeadCredentialType === FIRM_SERVICES.inspections);
        this.application.clearanceTesting = workspace.credentials.some((credential) => credential.firmLeadCredentialType === FIRM_SERVICES.clearanceTesting);
        this.application.leadHazardScreening = workspace.credentials.some((credential) => credential.firmLeadCredentialType === FIRM_SERVICES.leadHazardScreening);
        this.application.riskAssessments = workspace.credentials.some((credential) => credential.firmLeadCredentialType === FIRM_SERVICES.riskAssessments);
        this.application.abatement = workspace.credentials.some((credential) => credential.firmLeadCredentialType === FIRM_SERVICES.abatement);

        const requestedCertificate = this.data.previousApplication;
        if (requestedCertificate) {
          this.applyRenewalSelection(requestedCertificate);
        } else if (this.renewalOptions.length) {
          this.application.applicationType = this.renewalOptions[0].applicationType;
          this.application.previousApplication = this.renewalOptions[0].certificate.controlNumber ?? null;
        }

        this.patchFormsFromApplication();
        this.currentStep = 0;
        this.linkLookupLoading = false;
        this.validationSummaryRequested = false;
        this.loading = false;
      },
      error: () => {
        this.toastr.error('Unable to load the Firm renewal context.');
        this.dialogRef.close(false);
      }
    });
  }

  private loadSavedLinkSummary(firmId: number): void {
    forkJoin({
      firm: this.firmService.getFirm(firmId),
      addresses: this.firmService.getFirmAddresses(firmId).pipe(catchError(() => of([]))),
      primaryContacts: this.personService.GetAllFirmPOCPeopleContactInformation(firmId).pipe(catchError(() => of([]))),
      firmContacts: this.firmService.getFirmContactInformation(firmId).pipe(catchError(() => of([]))),
    }).subscribe({
      next: (result) => {
        this.linkSummary = {
          firm: result.firm,
          addresses: result.addresses,
          primaryContacts: result.primaryContacts,
          firmContacts: result.firmContacts,
        };
        this.linkLookupFailed = false;
        this.linkLookupMessage = '';
      },
      error: () => undefined,
    });
  }

  private createApplicationForm(): FormGroup {
    const phonePattern = /^\(?([0-9]{3})\)?[-\s]*([0-9]{3})[-\s]*([0-9]{4}).*$/;
    const zipPattern = /^\d{5}(-\d{4})?$/;

    const form = new FormGroup({
      applicationType: new FormControl(FIRM_APPLICATION_TYPES.lbpa, Validators.required),
      firmName: new FormControl('', Validators.required),
      ccb: new FormControl(''),
      renewal: new FormControl(false),
      previousApplication: new FormControl(''),
      isPublic: new FormControl(false),
      inspections: new FormControl(false),
      clearanceTesting: new FormControl(false),
      leadHazardScreening: new FormControl(false),
      riskAssessments: new FormControl(false),
      abatement: new FormControl(false),
      mailingIsPhysical: new FormControl(false),
      contactTitle: new FormControl(''),
      contactFirstName: new FormControl('', Validators.required),
      contactMiddleName: new FormControl(''),
      contactLastName: new FormControl('', Validators.required),
      contactPhone: new FormControl('', [Validators.required, Validators.pattern(phonePattern)]),
      contactAlternatePhone: new FormControl('', Validators.pattern(phonePattern)),
      contactFaxPhone: new FormControl('', Validators.pattern(phonePattern)),
      contactEmail: new FormControl('', [Validators.required, Validators.email]),
      contactConfirmEmail: new FormControl('', [Validators.required, Validators.email]),
      firmPhysicalState: new FormControl('OR', Validators.required),
      firmPhysicalStreet: new FormControl('', Validators.required),
      firmPhysicalCity: new FormControl('', Validators.required),
      firmPhysicalZip: new FormControl('', [Validators.required, Validators.pattern(zipPattern)]),
      firmPhysicalCounty: new FormControl(''),
      firmMailingStreet: new FormControl(''),
      firmMailingCity: new FormControl(''),
      firmMailingState: new FormControl('OR'),
      firmMailingZip: new FormControl('', Validators.pattern(zipPattern)),
      firmMailingCounty: new FormControl(''),
      firmPhone: new FormControl('', [Validators.required, Validators.pattern(phonePattern)]),
      firmAlternatePhone: new FormControl('', Validators.pattern(phonePattern)),
      firmFaxPhone: new FormControl('', Validators.pattern(phonePattern)),
      firmEmail: new FormControl('', [Validators.required, Validators.email]),
      firmConfirmEmail: new FormControl('', [Validators.required, Validators.email]),
      firmWebsite: new FormControl(''),
      firmServices: new FormControl(''),
      coverage: new FormControl(''),
    }, {
      validators: [
        this.commonService.emailValidator('contactEmail', 'contactConfirmEmail'),
        this.commonService.emailValidator('firmEmail', 'firmConfirmEmail'),
        this.requiredMailingFieldValidator('firmMailingStreet'),
        this.requiredMailingFieldValidator('firmMailingCity'),
        this.requiredMailingFieldValidator('firmMailingState'),
        this.requiredMailingFieldValidator('firmMailingZip'),
      ]
    });

    return form;
  }

  private createEmptyApplication(): IFirmApplication {
    return {
      id: 0,
      userId: null,
      firmId: null,
      firm: null,
      approverEmail: null,
      applicationViableDate: null,
      firmName: '',
      ccb: null,
      coverage: null,
      applicationStatus: APPLICATION_STATUS.inProgress,
      applicationDate: new Date().toISOString(),
      approvalDate: null,
      applicationType: this.selectedWorkflow === 'link' ? FIRM_APPLICATION_TYPES.link : FIRM_APPLICATION_TYPES.lbpa,
      renewal: this.selectedWorkflow === 'renewal',
      previousApplication: null,
      isPublic: false,
      firmServices: '',
      inspections: false,
      clearanceTesting: false,
      leadHazardScreening: false,
      riskAssessments: false,
      abatement: false,
      mailingIsPhysical: false,
      contactTitle: null,
      contactFirstName: null,
      contactMiddleName: null,
      contactLastName: null,
      contactPhone: null,
      contactAlternatePhone: null,
      contactFaxPhone: null,
      contactEmail: null,
      contactConfirmEmail: null,
      firmPhysicalStreet: null,
      firmPhysicalState: 'OR',
      firmPhysicalCity: null,
      firmPhysicalZip: null,
      firmPhysicalCounty: null,
      firmMailingStreet: null,
      firmMailingState: 'OR',
      firmMailingCity: null,
      firmMailingZip: null,
      firmMailingCounty: null,
      firmPhone: null,
      firmAlternatePhone: null,
      firmFaxPhone: null,
      firmEmail: null,
      firmConfirmEmail: null,
      firmWebsite: null,
      attestation: false,
      esignature: null,
      deficiencies: [],
    };
  }

  private createEmptyPerson(): IFirmApplicationPerson {
    return {
      id: 0,
      applicationId: this.application?.id ?? 0,
      person: null,
      firstName: '',
      lastName: '',
      certificateId: null,
      discipline: null,
      atp: null,
      trainingDate: null,
      firmIsPaying: false,
    };
  }

  private patchFormsFromApplication(): void {
    const applicationType = (this.application.applicationType === FIRM_APPLICATION_TYPES.rrp || this.application.applicationType === FIRM_APPLICATION_TYPES.lbpa)
      ? this.application.applicationType
      : FIRM_APPLICATION_TYPES.lbpa;
    const physicalState = this.application.firmPhysicalState || 'OR';
    const mailingState = this.application.firmMailingState || 'OR';

    this.applicationForm.patchValue({
      applicationType,
      firmName: this.application.firmName || '',
      ccb: this.application.ccb || '',
      renewal: this.application.renewal === true,
      previousApplication: this.application.previousApplication || '',
      isPublic: this.application.isPublic === true,
      inspections: this.application.inspections === true,
      clearanceTesting: this.application.clearanceTesting === true,
      leadHazardScreening: this.application.leadHazardScreening === true,
      riskAssessments: this.application.riskAssessments === true,
      abatement: this.application.abatement === true,
      mailingIsPhysical: this.application.mailingIsPhysical === true,
      contactTitle: this.application.contactTitle || '',
      contactFirstName: this.application.contactFirstName || '',
      contactMiddleName: this.application.contactMiddleName || '',
      contactLastName: this.application.contactLastName || '',
      contactPhone: this.application.contactPhone || '',
      contactAlternatePhone: this.application.contactAlternatePhone || '',
      contactFaxPhone: this.application.contactFaxPhone || '',
      contactEmail: this.application.contactEmail || '',
      contactConfirmEmail: this.application.contactConfirmEmail || '',
      firmPhysicalStreet: this.application.firmPhysicalStreet || '',
      firmPhysicalState: physicalState,
      firmPhysicalCity: this.application.firmPhysicalCity || '',
      firmPhysicalZip: this.application.firmPhysicalZip || '',
      firmPhysicalCounty: physicalState === 'OR' ? (this.application.firmPhysicalCounty || '') : '',
      firmMailingStreet: this.application.firmMailingStreet || '',
      firmMailingState: mailingState,
      firmMailingCity: this.application.firmMailingCity || '',
      firmMailingZip: this.application.firmMailingZip || '',
      firmMailingCounty: mailingState === 'OR' ? (this.application.firmMailingCounty || '') : '',
      firmPhone: this.application.firmPhone || '',
      firmAlternatePhone: this.application.firmAlternatePhone || '',
      firmFaxPhone: this.application.firmFaxPhone || '',
      firmEmail: this.application.firmEmail || '',
      firmConfirmEmail: this.application.firmConfirmEmail || '',
      firmWebsite: this.application.firmWebsite || '',
      firmServices: this.application.firmServices || '',
      coverage: this.application.coverage || '',
    });

    this.attestationForm.patchValue({
      attestation: this.application.attestation === true,
      esignature: this.application.esignature || '',
    });
  }

  private syncApplicationFromForms(): void {
    const rawValue = this.applicationForm.getRawValue();
    const attestationValue = this.attestationForm.getRawValue();

    this.application = {
      ...this.application,
      applicationStatus: this.application.applicationStatus || APPLICATION_STATUS.inProgress,
      applicationDate: this.application.applicationDate || new Date().toISOString(),
      applicationType: this.isLinkWorkflow ? FIRM_APPLICATION_TYPES.link : rawValue.applicationType,
      firmName: rawValue.firmName,
      ccb: rawValue.ccb || null,
      firmServices: rawValue.firmServices || null,
      coverage: rawValue.coverage || null,
      renewal: rawValue.renewal === true,
      previousApplication: rawValue.previousApplication || null,
      isPublic: rawValue.isPublic === true,
      inspections: rawValue.inspections === true,
      clearanceTesting: rawValue.clearanceTesting === true,
      leadHazardScreening: rawValue.leadHazardScreening === true,
      riskAssessments: rawValue.riskAssessments === true,
      abatement: rawValue.abatement === true,
      mailingIsPhysical: rawValue.mailingIsPhysical === true,
      contactTitle: rawValue.contactTitle || null,
      contactFirstName: rawValue.contactFirstName || null,
      contactMiddleName: rawValue.contactMiddleName || null,
      contactLastName: rawValue.contactLastName || null,
      contactPhone: rawValue.contactPhone || null,
      contactAlternatePhone: rawValue.contactAlternatePhone || null,
      contactFaxPhone: rawValue.contactFaxPhone || null,
      contactEmail: rawValue.contactEmail || null,
      contactConfirmEmail: rawValue.contactConfirmEmail || null,
      firmPhysicalStreet: rawValue.firmPhysicalStreet || null,
      firmPhysicalState: rawValue.firmPhysicalState || null,
      firmPhysicalCity: rawValue.firmPhysicalCity || null,
      firmPhysicalZip: rawValue.firmPhysicalZip || null,
      firmPhysicalCounty: rawValue.firmPhysicalState === 'OR' ? (rawValue.firmPhysicalCounty || null) : null,
      firmMailingStreet: rawValue.mailingIsPhysical ? rawValue.firmPhysicalStreet : (rawValue.firmMailingStreet || null),
      firmMailingState: rawValue.mailingIsPhysical ? rawValue.firmPhysicalState : (rawValue.firmMailingState || null),
      firmMailingCity: rawValue.mailingIsPhysical ? rawValue.firmPhysicalCity : (rawValue.firmMailingCity || null),
      firmMailingZip: rawValue.mailingIsPhysical ? rawValue.firmPhysicalZip : (rawValue.firmMailingZip || null),
      firmMailingCounty: rawValue.mailingIsPhysical
        ? (rawValue.firmPhysicalState === 'OR' ? (rawValue.firmPhysicalCounty || null) : null)
        : (rawValue.firmMailingState === 'OR' ? (rawValue.firmMailingCounty || null) : null),
      firmPhone: rawValue.firmPhone || null,
      firmAlternatePhone: rawValue.firmAlternatePhone || null,
      firmFaxPhone: rawValue.firmFaxPhone || null,
      firmEmail: rawValue.firmEmail || null,
      firmConfirmEmail: rawValue.firmConfirmEmail || null,
      firmWebsite: rawValue.firmWebsite || null,
      attestation: attestationValue.attestation === true,
      esignature: attestationValue.esignature || null,
    };
  }

  private persistApplication(nextStatus: string, successMessage: string | null, continuation?: () => void): void {
    this.saving = true;
    this.application.applicationStatus = nextStatus;

    this.firmService.saveFirmApplication(this.application).subscribe({
      next: (savedApplication) => {
        this.application = savedApplication;
        this.patchFormsFromApplication();
        this.applicationForm.markAsPristine();
        this.attestationForm.markAsPristine();
        this.didPersistChange = true;
        this.saving = false;

        if (successMessage) {
          this.toastr.success(successMessage);
        }

        if (continuation) {
          continuation();
        }
      },
      error: () => {
        this.saving = false;
        this.toastr.error('Unable to save the Firm application.');
      }
    });
  }

  private ensureApplicationSaved(continuation: () => void): void {
    if (this.application.id > 0) {
      continuation();
      return;
    }

    this.saveDraft(false, continuation);
  }

  private saveDocumentFile(file: File, description: string | null, applicationPersonId: number | null): void {
    this.documentService
      .uploadDocument(
        file,
        { ownerType: 'FirmApplication', ownerId: this.application.id, firmApplicationPersonId: applicationPersonId ?? undefined },
        DOCUMENT_CATEGORIES.firmApplication,
        description ?? undefined)
      .subscribe({
        next: (savedDocument) => {
          this.setDocuments([...this.documents, savedDocument]);
          this.didPersistChange = true;
        },
        error: (error) => this.toastr.error(error?.error ?? 'Unable to save the application document.'),
      });
  }

  private openPersonDialog(person: IFirmApplicationPerson): void {
    if (!this.canEdit) {
      return;
    }

    const dialogRef = this.dialog.open(FirmApplicationPersonDialogComponent, {
      width: this.sharedService.isMobile ? '95%' : '720px',
      autoFocus: false,
      data: {
        person,
        applicationType: this.applicationForm.get('applicationType')?.value || FIRM_APPLICATION_TYPES.lbpa,
        existingPeople: this.people,
      }
    });

    dialogRef.afterClosed().subscribe((result: IFirmApplicationPerson | undefined) => {
      if (!result) {
        return;
      }

      this.ensureApplicationSaved(() => {
        this.firmService.saveFirmApplicationPerson({ ...result, applicationId: this.application.id }).subscribe({
          next: (savedPerson) => {
            const existingIndex = this.people.findIndex((currentPerson) => currentPerson.id === savedPerson.id);
            const updatedPeople = [...this.people];
            if (existingIndex >= 0) {
              updatedPeople[existingIndex] = savedPerson;
            } else {
              updatedPeople.push(savedPerson);
            }

            this.setPeople(updatedPeople);
            this.didPersistChange = true;
          },
          error: () => this.toastr.error('Unable to save the certified individual.'),
        });
      });
    });
  }

  private applyLinkedFirmToApplication(): void {
    if (!this.linkSummary) {
      return;
    }

    const physicalAddress = this.linkSummary.addresses.find((address) => address.isCurrentPhysical)?.address;
    const mailingAddress = this.linkSummary.addresses.find((address) => address.isCurrentMailing)?.address;
    const primaryContact = this.linkSummary.primaryContacts[0];

    this.application.firm = this.linkSummary.firm;
    this.application.firmId = this.linkSummary.firm.id;
    this.application.firmName = this.linkSummary.firm.name;
    this.application.ccb = this.linkSummary.firm.ccb ?? null;
    this.application.isPublic = this.linkSummary.firm.isPrivate === true;
    this.application.firmServices = this.linkSummary.firm.servicesAvailable ?? null;
    this.application.coverage = this.linkSummary.firm.coverage ?? null;
    this.application.contactTitle = primaryContact?.person?.title ?? null;
    this.application.contactFirstName = primaryContact?.person?.firstName ?? null;
    this.application.contactMiddleName = primaryContact?.person?.middleName ?? null;
    this.application.contactLastName = primaryContact?.person?.lastName ?? null;
    this.application.contactPhone = primaryContact?.contactInformation?.phone ?? null;
    this.application.contactAlternatePhone = primaryContact?.contactInformation?.cell ?? null;
    this.application.contactFaxPhone = primaryContact?.contactInformation?.fax ?? null;
    this.application.contactEmail = primaryContact?.contactInformation?.email ?? null;
    this.application.contactConfirmEmail = primaryContact?.contactInformation?.email ?? null;
    this.application.firmPhysicalStreet = physicalAddress?.street ?? null;
    this.application.firmPhysicalState = physicalAddress?.state ?? null;
    this.application.firmPhysicalCity = physicalAddress?.city ?? null;
    this.application.firmPhysicalZip = physicalAddress?.zip ?? null;
    this.application.firmPhysicalCounty = physicalAddress?.county ?? null;
    this.application.firmMailingStreet = mailingAddress?.street ?? null;
    this.application.firmMailingState = mailingAddress?.state ?? null;
    this.application.firmMailingCity = mailingAddress?.city ?? null;
    this.application.firmMailingZip = mailingAddress?.zip ?? null;
    this.application.firmMailingCounty = mailingAddress?.county ?? null;
    this.application.mailingIsPhysical = !!physicalAddress && !!mailingAddress
      && physicalAddress.street === mailingAddress.street
      && physicalAddress.city === mailingAddress.city
      && physicalAddress.state === mailingAddress.state
      && physicalAddress.zip === mailingAddress.zip;

    this.patchFormsFromApplication();
  }

  private getInitialStepIndex(): number {
    if (this.isLinkWorkflow) {
      return this.application?.firmId ? this.getLastStepIndex() : 0;
    }

    if (this.application?.id && this.isSubmitted) {
      return this.getLastStepIndex();
    }

    return 0;
  }

  private resolveInitialWorkflow(): IFirmApplicationWorkflow {
    if (this.data.workflow === 'renewal') {
      return 'renewal';
    }

    if (this.data.workflow === 'link') {
      return 'link';
    }

    if (this.data.workflow === 'new') {
      return 'new';
    }

    return 'chooser';
  }

  private beginWorkflow(workflow: 'new' | 'link'): void {
    this.selectedWorkflow = workflow;
    this.resetWorkflowState();
  }

  private resetWorkflowState(): void {
    this.application = this.createEmptyApplication();
    this.peopleFilterString = '';
    this.documentsFilterString = '';
    this.setPeople([]);
    this.setDocuments([]);
    this.linkSearchResults = [];
    this.linkLookupLoading = false;
    this.renewalOptions = [];
    this.currentStep = 0;
    this.validationSummaryRequested = false;
    this.resetLinkSelectionState();
    this.patchFormsFromApplication();
    this.applicationForm.enable({ emitEvent: false });
    this.attestationForm.enable({ emitEvent: false });
    this.attestationForm.reset({ attestation: false, esignature: '' }, { emitEvent: false });
    this.applicationForm.markAsPristine();
    this.applicationForm.markAsUntouched();
    this.attestationForm.markAsPristine();
    this.attestationForm.markAsUntouched();
  }

  private navigateToStep(stepIndex: number): void {
    const boundedStep = Math.max(0, Math.min(stepIndex, this.getLastStepIndex()));
    const previousStep = this.currentStep;

    if (boundedStep === previousStep) {
      return;
    }

    this.currentStep = boundedStep;

    if (
      this.isLinkWorkflow
      && previousStep === 0
      && boundedStep > 0
      && this.hasText(this.applicationForm.get('firmName')?.value)
    ) {
      this.loadLinkFirmDetails();
    }
  }

  private getLastStepIndex(): number {
    return this.getStepLabels().length - 1;
  }

  private getStepLabels(): string[] {
    if (!this.isLinkWorkflow) {
      return this.standardStepLabels;
    }

    return this.isSubmitted ? this.submittedLinkStepLabels : this.linkStepLabels;
  }

  private isSubmittedLinkConfirmationStep(): boolean {
    return this.isLinkWorkflow && this.isSubmitted && this.currentStep === this.getLastStepIndex();
  }

  private getLinkApprovalRecipientSummary(): string {
    const approvalEmails = this.linkApprovalContactEmails;

    if (!approvalEmails.length) {
      return 'No valid contact email was found for this Firm. This request will be sent to <strong>leadprogram@odhsoha.oregon.gov</strong> for staff review.';
    }

    if (approvalEmails.length === 1) {
      return `An approval email will be sent to the contact email on file: <strong>${approvalEmails[0]}</strong>.`;
    }

    return `Approval emails will be sent to the contact emails on file:<br>${approvalEmails.map((email) => `<strong>${email}</strong>`).join('<br>')}`;
  }

  private hasActiveFirmAffiliation(firmId: number, affiliations: any[]): boolean {
    if (!firmId || firmId <= 0) {
      return false;
    }

    return (affiliations || []).some((affiliation) => {
      const status = (affiliation?.status || '').toString().trim().toLowerCase();
      const affiliationFirmId = Number(affiliation?.firm?.id || 0);
      return status === 'active' && affiliationFirmId === firmId;
    });
  }

  private guardDuplicateFirmLinkSubmission(firmId: number, continuation: () => void): void {
    this.commonService.getAccountAffiliation().subscribe({
      next: (affiliations) => {
        if (this.hasActiveFirmAffiliation(firmId, affiliations)) {
          this.toastr.warning('Your account is already linked to this Firm.', 'Already Linked');
          return;
        }

        continuation();
      },
      error: () => continuation(),
    });
  }

  private hasText(value: any): boolean {
    return (value ?? '').toString().trim().length > 0;
  }

  private resolveValidationFieldSelectors(error: IWizardValidationError): string[] {
    if (this.applicationForm.get(error.field) || this.attestationForm.get(error.field)) {
      return this.buildControlSelectors(error.field);
    }

    if (error.field === 'linkLookup' || error.field === 'linkSummary') {
      return this.buildControlSelectors('firmName');
    }

    if (error.field.startsWith('applicationPerson')) {
      return ['[data-focus-id="add-existing-individual"]'];
    }

    if (error.field.startsWith('applicationDocument')) {
      return ['[data-focus-id="upload-document"]'];
    }

    return [];
  }

  private buildControlSelectors(controlName: string): string[] {
    return [
      `[formcontrolname="${controlName}"]`,
      `[formControlName="${controlName}"]`,
    ];
  }

  private getDefaultInvalidFieldSelectors(): string[] {
    return [
      '.wizard-shell .scroll-container .field-error input',
      '.wizard-shell .scroll-container .field-error textarea',
      '.wizard-shell .scroll-container .field-error .mat-select-trigger',
      '.wizard-shell .scroll-container .mat-form-field-invalid input',
      '.wizard-shell .scroll-container .mat-form-field-invalid textarea',
      '.wizard-shell .scroll-container .mat-form-field-invalid .mat-select-trigger',
      '.wizard-shell .scroll-container .mat-checkbox.ng-invalid input',
    ];
  }

  private focusFirstVisibleBySelector(selectors: string[]): boolean {
    for (const selector of selectors) {
      const element = this.findFirstVisibleElement(selector);
      if (!element) {
        continue;
      }

      this.focusElement(element);
      return true;
    }

    return false;
  }

  private findFirstVisibleElement(selector: string): HTMLElement | null {
    if (typeof document === 'undefined') {
      return null;
    }

    const elements = Array.from(document.querySelectorAll(selector)) as HTMLElement[];
    return elements.find((element) => this.isVisibleElement(element)) || null;
  }

  private isVisibleElement(element: HTMLElement): boolean {
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
  }

  private focusElement(element: HTMLElement): void {
    const focusTarget = element.matches('input, textarea, select, button, [tabindex]')
      ? element
      : element.querySelector<HTMLElement>('input, textarea, select, button, .mat-select-trigger, .mat-radio-input, [tabindex]');

    if (!focusTarget) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    focusTarget.focus({ preventScroll: true });
    focusTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  private getVisibleScrollContainer(): HTMLElement | null {
    if (typeof document === 'undefined') {
      return null;
    }

    const containers = Array.from(document.querySelectorAll('.wizard-shell .scroll-container')) as HTMLElement[];
    return containers.find((container) => this.isVisibleElement(container)) || null;
  }

  private hasValidationInteraction(control: AbstractControl | null): boolean {
    return !!control && (this.validationSummaryRequested || control.touched || control.dirty);
  }

  private addControlError(errors: IWizardValidationError[], control: AbstractControl | null, field: string, defaultLabel: string, step: string): void {
    if (!control || !control.invalid || !this.hasValidationInteraction(control)) {
      return;
    }

    errors.push({
      field,
      label: this.getValidationLabel(control, defaultLabel),
      step,
    });
  }

  private addCustomError(errors: IWizardValidationError[], condition: boolean, field: string, label: string, step: string): void {
    if (!condition) {
      return;
    }

    errors.push({ field, label, step });
  }

  private getValidationLabel(control: AbstractControl, defaultLabel: string): string {
    if (control.hasError('linkNotFound')) {
      return this.linkLookupMessage || defaultLabel;
    }

    if (control.hasError('emailMismatch')) {
      return defaultLabel + ' must match';
    }

    if (control.hasError('email')) {
      return defaultLabel + ' must be a valid email address';
    }

    if (control.hasError('pattern')) {
      if (defaultLabel.toLowerCase().includes('zip')) {
        return defaultLabel + ' must be a valid ZIP code';
      }

      if (defaultLabel.toLowerCase().includes('phone')) {
        return defaultLabel + ' must be a valid phone number';
      }

      return defaultLabel + ' format is invalid';
    }

    return defaultLabel;
  }

  private getValidationErrors(): IWizardValidationError[] {
    const errors: IWizardValidationError[] = [];

    if (this.showWorkflowChooser) {
      return errors;
    }

    if (this.isLinkWorkflow) {
      this.addControlError(errors, this.applicationForm.get('firmName'), 'firmName', 'Firm Name', 'Firm Information');
      this.addCustomError(
        errors,
        this.linkLookupFailed && this.hasValidationInteraction(this.applicationForm.get('firmName')),
        'linkLookup',
        this.linkLookupMessage || 'Select a matching Firm',
        'Firm Information'
      );
      this.addCustomError(
        errors,
        !this.linkSummary && this.currentStep > 0 && this.validationSummaryRequested,
        'linkSummary',
        'Select a matching Firm',
        'Firm Information'
      );
      this.addControlError(errors, this.attestationForm.get('attestation'), 'attestation', 'Attestation', 'Signature and Acknowledgments');
      this.addControlError(errors, this.attestationForm.get('esignature'), 'esignature', 'Electronic Signature', 'Signature and Acknowledgments');
      return this.dedupeValidationErrors(errors);
    }

    this.addControlError(errors, this.applicationForm.get('applicationType'), 'applicationType', 'Application Type', 'Application Information');
    this.addCustomError(
      errors,
      this.isRenewalApplication
        && !this.hasText(this.applicationForm.get('previousApplication')?.value)
        && this.hasValidationInteraction(this.applicationForm.get('previousApplication')),
      'previousApplication',
      'Certificate to Renew',
      'Application Information'
    );

    const firmInformationControls: Array<[string, string]> = [
      ['firmName', 'Firm Name'],
      ['contactFirstName', 'Contact First Name'],
      ['contactLastName', 'Contact Last Name'],
      ['contactPhone', 'Contact Primary Phone'],
      ['contactEmail', 'Contact Email'],
      ['contactConfirmEmail', 'Contact Confirm Email'],
      ['firmPhysicalStreet', 'Physical Street Address'],
      ['firmPhysicalCity', 'Physical City'],
      ['firmPhysicalState', 'Physical State'],
      ['firmPhysicalZip', 'Physical ZIP Code'],
      ['firmPhone', 'Firm Primary Phone'],
      ['firmEmail', 'Firm Email'],
      ['firmConfirmEmail', 'Firm Confirm Email'],
    ];

    firmInformationControls.forEach(([controlName, label]) => {
      this.addControlError(errors, this.applicationForm.get(controlName), controlName, label, 'Firm Information');
    });

    this.addCustomError(
      errors,
      this.showPhysicalCounty
        && !this.hasText(this.applicationForm.get('firmPhysicalCounty')?.value)
        && this.hasValidationInteraction(this.applicationForm.get('firmPhysicalCounty')),
      'firmPhysicalCounty',
      'Physical County',
      'Firm Information'
    );

    if (this.showMailingAddress) {
      this.addCustomError(
        errors,
        !this.hasText(this.applicationForm.get('firmMailingStreet')?.value) && this.hasValidationInteraction(this.applicationForm.get('firmMailingStreet')),
        'firmMailingStreet',
        'Mailing Street Address',
        'Firm Information'
      );
      this.addCustomError(
        errors,
        !this.hasText(this.applicationForm.get('firmMailingCity')?.value) && this.hasValidationInteraction(this.applicationForm.get('firmMailingCity')),
        'firmMailingCity',
        'Mailing City',
        'Firm Information'
      );
      this.addCustomError(
        errors,
        !this.hasText(this.applicationForm.get('firmMailingState')?.value) && this.hasValidationInteraction(this.applicationForm.get('firmMailingState')),
        'firmMailingState',
        'Mailing State',
        'Firm Information'
      );
      this.addCustomError(
        errors,
        !this.hasText(this.applicationForm.get('firmMailingZip')?.value) && this.hasValidationInteraction(this.applicationForm.get('firmMailingZip')),
        'firmMailingZip',
        'Mailing ZIP Code',
        'Firm Information'
      );

      this.addCustomError(
        errors,
        this.showMailingCounty
          && !this.hasText(this.applicationForm.get('firmMailingCounty')?.value)
          && this.hasValidationInteraction(this.applicationForm.get('firmMailingCounty')),
        'firmMailingCounty',
        'Mailing County',
        'Firm Information'
      );
    }

    this.addCertifiedIndividualValidationErrors(errors);
    this.addDocumentValidationErrors(errors);

    this.addControlError(errors, this.attestationForm.get('attestation'), 'attestation', 'Attestation', 'Review & Submit');
    this.addControlError(errors, this.attestationForm.get('esignature'), 'esignature', 'Electronic Signature', 'Review & Submit');

    return this.dedupeValidationErrors(errors);
  }

  private getStepValidationErrors(stepIndex: number): IWizardValidationError[] {
    const targetStep = this.getStepLabels()[stepIndex];
    return this.getValidationErrors().filter((error) => error.step === targetStep);
  }

  private dedupeValidationErrors(errors: IWizardValidationError[]): IWizardValidationError[] {
    const seen = new Set<string>();
    return errors.filter((error) => {
      const key = error.step + '|' + error.field + '|' + error.label;
      if (seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    });
  }

  private handleValidationFailure(errors: IWizardValidationError[], message: string): void {
    this.validationSummaryRequested = true;
    this.isFeedbackCollapsed = false;

    if (errors.length > 0) {
      this.toastr.error(message + ' ' + errors.length + ' item(s) need attention.', 'Validation Error', {
        timeOut: 5000,
      });
      this.scrollValidationSummaryIntoView();
      return;
    }

    this.toastr.error(message, 'Validation Error', {
      timeOut: 5000,
    });
  }

  private scrollValidationSummaryIntoView(): void {
    setTimeout(() => {
      this.validationSummaryCard?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }

  private clearLinkLookupError(): void {
    this.linkLookupFailed = false;
    this.linkLookupMessage = '';

    const control = this.applicationForm.get('firmName');
    const existingErrors = control?.errors;
    if (!control || !existingErrors || !existingErrors['linkNotFound']) {
      return;
    }

    const { linkNotFound, ...remainingErrors } = existingErrors;
    control.setErrors(Object.keys(remainingErrors).length > 0 ? remainingErrors : null);
  }

  private setLinkLookupError(message: string): void {
    const control = this.applicationForm.get('firmName');
    const nextErrors = { ...(control?.errors || {}), linkNotFound: true };

    this.linkLookupFailed = true;
    this.linkLookupMessage = message;
    control?.setErrors(nextErrors);
  }

  private resetLinkSelectionState(): void {
    this.linkSummary = null;
    this.linkLookupLoading = false;
    this.clearLinkedFirmSelection();
    this.clearLinkLookupError();
  }

  private clearLinkedFirmSelection(): void {
    const preservedFirmName = (this.applicationForm.get('firmName')?.value || this.application?.firmName || '').toString();

    this.application.firm = null;
    this.application.firmId = null;
    this.application.firmName = preservedFirmName;
    this.application.ccb = null;
    this.application.coverage = null;
    this.application.isPublic = false;
    this.application.firmServices = null;
    this.application.contactTitle = null;
    this.application.contactFirstName = null;
    this.application.contactMiddleName = null;
    this.application.contactLastName = null;
    this.application.contactPhone = null;
    this.application.contactAlternatePhone = null;
    this.application.contactFaxPhone = null;
    this.application.contactEmail = null;
    this.application.contactConfirmEmail = null;
    this.application.firmPhysicalStreet = null;
    this.application.firmPhysicalState = 'OR';
    this.application.firmPhysicalCity = null;
    this.application.firmPhysicalZip = null;
    this.application.firmPhysicalCounty = null;
    this.application.firmMailingStreet = null;
    this.application.firmMailingState = 'OR';
    this.application.firmMailingCity = null;
    this.application.firmMailingZip = null;
    this.application.firmMailingCounty = null;
    this.application.mailingIsPhysical = false;
    this.application.firmPhone = null;
    this.application.firmAlternatePhone = null;
    this.application.firmFaxPhone = null;
    this.application.firmEmail = null;
    this.application.firmConfirmEmail = null;
    this.application.firmWebsite = null;

    this.applicationForm.patchValue({
      firmName: preservedFirmName,
      ccb: '',
      isPublic: false,
      firmServices: '',
      coverage: '',
      contactTitle: '',
      contactFirstName: '',
      contactMiddleName: '',
      contactLastName: '',
      contactPhone: '',
      contactAlternatePhone: '',
      contactFaxPhone: '',
      contactEmail: '',
      contactConfirmEmail: '',
      firmPhysicalStreet: '',
      firmPhysicalState: 'OR',
      firmPhysicalCity: '',
      firmPhysicalZip: '',
      firmPhysicalCounty: '',
      firmMailingStreet: '',
      firmMailingState: 'OR',
      firmMailingCity: '',
      firmMailingZip: '',
      firmMailingCounty: '',
      mailingIsPhysical: false,
      firmPhone: '',
      firmAlternatePhone: '',
      firmFaxPhone: '',
      firmEmail: '',
      firmConfirmEmail: '',
      firmWebsite: '',
    }, { emitEvent: false });
  }

  private selectedLinkFirmNameMatches(searchTerm: string): boolean {
    return (this.linkSummary?.firm?.name || '').trim().toLowerCase() === searchTerm.trim().toLowerCase();
  }

  private buildRenewalOptions(certificates: IFirmCertificate[]): IRenewalOption[] {
    return certificates
      .filter((certificate) => !!certificate.controlNumber)
      .map((certificate) => ({
        label: (certificate.codeType || 'Firm Certificate') + ' - ' + certificate.controlNumber,
        certificate,
        applicationType: (certificate.codeType || '').toLowerCase().includes('rrp')
          ? FIRM_APPLICATION_TYPES.rrp
          : FIRM_APPLICATION_TYPES.lbpa,
      }));
  }

  private requiredMailingFieldValidator(fieldName: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      if (control.get('mailingIsPhysical')?.value === true) {
        return null;
      }

      const value = control.get(fieldName)?.value;
      return value ? null : { [fieldName + 'Required']: true };
    };
  }

  private isDraftStatus(status: string | null): boolean {
    return status === APPLICATION_STATUS.inProgress || status === APPLICATION_STATUS.actionNeeded || !status;
  }

  private isApplicationExpired(application: IFirmApplication | null | undefined): boolean {
    if (!application?.applicationDate) {
      return false;
    }

    const status = this.normalizeStatus(application.applicationStatus);
    if (status === this.normalizeStatus(APPLICATION_STATUS.approved) || status === this.normalizeStatus(APPLICATION_STATUS.cancelled)) {
      return false;
    }

    const currentDate = this.toDateOnly(new Date());
    const applicationDate = this.toDateOnly(new Date(application.applicationDate));
    const diffInDays = (currentDate.getTime() - applicationDate.getTime()) / (1000 * 3600 * 24);
    return diffInDays > 90;
  }

  private normalizeStatus(status: string | null | undefined): string {
    return (status || '').toString().trim().toLowerCase();
  }

  private toDateOnly(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private addCertifiedIndividualValidationErrors(errors: IWizardValidationError[]): void {
    const seenPersonIds = new Set<number>();

    this.people.forEach((person, index) => {
      const displayName = this.getApplicationPersonDisplayName(person) || 'Certified Individual ' + (index + 1);

      this.addCustomError(
        errors,
        !person.person?.id,
        'applicationPersonSelection-' + index,
        displayName + ' must reference an existing person',
        'Certified Individuals'
      );

      if (person.person?.id) {
        this.addCustomError(
          errors,
          seenPersonIds.has(person.person.id),
          'applicationPersonDuplicate-' + person.person.id,
          displayName + ' has already been added',
          'Certified Individuals'
        );
        seenPersonIds.add(person.person.id);
      }

      if (!this.showLbpaFields) {
        this.addCustomError(
          errors,
          !this.hasText(person.atp),
          'applicationPersonAtp-' + index,
          displayName + ' training provider',
          'Certified Individuals'
        );
        this.addCustomError(
          errors,
          !this.hasText(person.trainingDate),
          'applicationPersonTrainingDate-' + index,
          displayName + ' training date',
          'Certified Individuals'
        );
      }
    });
  }

  private addDocumentValidationErrors(errors: IWizardValidationError[]): void {
    this.documents.forEach((document, index) => {
      const documentLabel = this.hasText(document.fileName) ? document.fileName : 'Document ' + (index + 1);

      this.addCustomError(
        errors,
        !this.hasText(document.fileName),
        'applicationDocumentName-' + index,
        'Document file name',
        'Documents'
      );

      this.addCustomError(
        errors,
        !!document.firmApplicationPersonId && !this.people.some((person) => person.id === document.firmApplicationPersonId),
        'applicationDocumentAssociation-' + index,
        documentLabel + ' is associated with a missing certified individual',
        'Documents'
      );
    });
  }

  private getApplicationPersonDisplayName(person: IFirmApplicationPerson): string {
    const firstName = person.person?.firstName || person.firstName || '';
    const lastName = person.person?.lastName || person.lastName || '';
    return (firstName + ' ' + lastName).trim();
  }

  private setPeople(people: IFirmApplicationPerson[]): void {
    this.people = [...people].sort((left, right) => {
      return this.getApplicationPersonDisplayName(left).localeCompare(this.getApplicationPersonDisplayName(right));
    });

    this.filterPeopleTable();
    this.filterDocumentTable();
  }

  private setDocuments(documents: IDocument[]): void {
    this.documents = [...documents].sort((left, right) => {
      return (left.fileName || '').localeCompare(right.fileName || '');
    });

    this.filterDocumentTable();
  }
}